import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { LoginVm } from '../../../shared/generated/Models/LoginVm';
import { SecurityService } from '../../../core/security/security.service';
import { SystemMessageService, SystemMessage } from '../../../core/system-message/system-message-service';
import { validateForm } from '../../../shared/form-elements/form-validateForm.function';
import { WebAuthnService } from '../../security/multi-factor-auth/web-authn.service';
import { MultiFactorAuthModalComponent } from './multi-factor-auth-modal/multi-factor-auth-modal.component';

@Component({
    selector: 'pcg-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnDestroy, OnInit, AfterViewInit {
	@ViewChild('myForm', { static: true }) myForm: ElementRef;

	loginForm = LoginVm.Form;
	subscriptions: Subscription = new Subscription();

	constructor(
		private route: ActivatedRoute,
		private ms: SystemMessageService,
		private sec: SecurityService,
		private router: Router,
		private modalService: NgbModal,
		private webAuthn: WebAuthnService,
		title: Title
	) {
		title.setTitle('Login');
	}

	ngOnInit() {
		const user = this.sec.getUser();
		if (user !== null) {
			this.loginRedirect();
		}
	}

	ngAfterViewInit() {}

	onSubmit() {
		if (this.loginForm.valid) {
			this.sec.setLocalStorage('lastAccess', new Date());
			this.subscriptions.add(
				this.ms.getHttpObservable(this, 'api/Account/Login', this.loginForm).subscribe(msg => {
					this.doLogin(msg);
				})
			);
		} else {
			validateForm(this.loginForm);
		}
	}

	doLogin(msg: SystemMessage) {
		const val = msg.value;
		if (val === 'CreateNewPassword') {
			this.passwordChangeRedirect();
			return;
		}
		if (!val) {
			return;
		}
		if (val.hasTwoFactorAuth) {
			// Need this hack to avoid change detection error on modal open
			this.myForm.nativeElement.ownerDocument.activeElement.blur();
			// Open the 2FA modal
			this.modalService.open(MultiFactorAuthModalComponent);
			// Handle 2FA
			this.doTwoFactorAuth();
			return;
		}
		this.loginForm.reset();
		this.sec.setSecurity(val.token, val.user, val.security);
		this.loginRedirect();
	}

	passwordChangeRedirect() {
		this.router.navigate(['/change-old-password']);
	}

	loginRedirect() {
		let redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl') || sessionStorage.getItem('redirectUrl');
		if (redirectUrl) {
			const baseUrl = document.getElementsByTagName('base')[0].href;
			redirectUrl = redirectUrl.replace(/^\//, '');
			if (redirectUrl.startsWith('uploads/')) {
				window.location.href = baseUrl + redirectUrl;
			} else {
				this.router.navigateByUrl(redirectUrl);
				sessionStorage.removeItem('redirectUrl');
			}
		} else {
			this.router.navigate(['/navient']);
		}
	}

	doTwoFactorAuth() {
		if (!this.webAuthn.isFidoCompatible()) {
			this.modalService.dismissAll();
			return;
		}

		// Save user credentials and clear form
		const userName = this.loginForm.value.userName;
		const password = this.loginForm.value.password;
		this.loginForm.reset();

		// Get assertion options from the server for user
		this.subscriptions.add(
			this.webAuthn.getAssertionOptions(userName, password).subscribe(makeAssertionOptions => {
				navigator['credentials']
					.get({ publicKey: makeAssertionOptions })
					.then(credential => {
						this.sec.setLocalStorage('lastAccess', new Date());
						this.webAuthn
							.verifyAssertion(credential, userName, password)
							.subscribe((msg: SystemMessage) => {
								this.modalService.dismissAll();
								this.doLogin(msg);
							});
					})
					.catch(err => {
						this.modalService.dismissAll();
						// Do this so there is a failed login history rercord:
						this.webAuthn.verifyAssertion(null, userName, password).subscribe((msg: SystemMessage) => {
							this.doLogin(msg);
						});
					});
			})
		);
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}
}
